/**
 * Office upgrades catalogue.
 *
 * Once the office is unlocked (3+ support roles hired OR inv-office-01
 * purchased), suppliers send opportunistic email offers — ~1 every
 * 30-60 days — proposing add-on equipment that boosts driver / support
 * staff satisfaction at the cost of an additional monthly charge.
 *
 * Accepting an offer :
 *   • Adds the upgrade id to `state.officeUpgrades` (object, idempotent)
 *   • Triggers a one-shot cash outflow (`costBuy`)
 *   • Adds `chargeMonthly` to the office monthly cost
 *   • Boosts the company-wide satisfaction (drivers + support) by
 *     `satisfactionBoost` (a small permanent perk surfaced on each driver
 *     tick by `getOfficeSatisfactionBoost`)
 *
 * Suppliers send proposals via `pushCustomMessage` so they appear in the
 * messagerie alongside the rest of the narrative. The player can ignore
 * the email (free) or click "Accepter" to apply the upgrade.
 */
export const OFFICE_UPGRADES = [
  {
    id: 'office-cleaning',
    name: 'Service de ménage hebdomadaire',
    supplier: 'NetPro Services',
    description: "Une équipe de 2 agents passe chaque vendredi soir pour nettoyer bureaux, sanitaires et salle de pause. L'environnement gagne en qualité — confort des équipes ressenti dès la 1ère semaine.",
    costBuy: 600,
    chargeMonthly: 480,
    satisfactionBoost: 1,
    icon: '🧹',
  },
  {
    id: 'office-fountain',
    name: 'Fontaine à eau bonbonne',
    supplier: 'AquaPro Distribution',
    description: "Installation d'une fontaine à eau filtrée + bonbonnes (4 par mois). Petits gestes mais effet réel sur le moral en été et pendant les longs débriefs.",
    costBuy: 350,
    chargeMonthly: 95,
    satisfactionBoost: 1,
    icon: '💧',
  },
  {
    id: 'office-coffee',
    name: 'Machine à café professionnelle',
    supplier: 'CaffèTime',
    description: "Machine à grains haut de gamme + entretien mensuel + grains illimités. Le café partagé reste le 1er lubrifiant social d'une équipe.",
    costBuy: 1800,
    chargeMonthly: 240,
    satisfactionBoost: 2,
    icon: '☕',
  },
  {
    id: 'office-restroom',
    name: 'Salle de repos équipée',
    supplier: 'ComfortLine',
    description: "Aménagement d'une pièce dédiée : canapé, baby-foot, micro-ondes, frigo. Refuge sacré entre deux missions, surtout pour les chauffeurs de passage.",
    costBuy: 4200,
    chargeMonthly: 180,
    satisfactionBoost: 3,
    icon: '🛋️',
  },
  {
    id: 'office-plants',
    name: 'Plantes vertes & décoration',
    supplier: 'GreenOffice',
    description: "Une dizaine de plantes vertes entretenues mensuellement + relooking déco. Petit budget, grand effet sur la perception de l'entreprise par les visiteurs et candidats.",
    costBuy: 800,
    chargeMonthly: 110,
    satisfactionBoost: 1,
    icon: '🪴',
  },
  {
    id: 'office-canteen',
    name: 'Tickets-restaurant subventionnés',
    supplier: 'Carte Resto+',
    description: "Mise en place de tickets-restaurant pour tous les salariés (10 € / jour ouvré). Avantage social hautement apprécié — devient quasi-attendu chez les grandes entreprises.",
    costBuy: 0,
    chargeMonthly: 850,
    satisfactionBoost: 4,
    icon: '🥪',
  },
  {
    id: 'office-gym',
    name: 'Abonnement salle de sport groupé',
    supplier: 'BienEtre Pro',
    description: "Convention avec une chaîne de salles de sport — accès illimité pour toute l'équipe. Bonus rétention sur les chauffeurs qui placent le bien-être physique en priorité.",
    costBuy: 0,
    chargeMonthly: 380,
    satisfactionBoost: 2,
    icon: '🏋️',
  },
];

export const OFFICE_UPGRADE_BY_ID = Object.fromEntries(
  OFFICE_UPGRADES.map(u => [u.id, u])
);

/**
 * Aggregate monthly cost of all owned office upgrades.
 */
export function getOfficeUpgradesMonthlyCost(officeUpgrades = {}) {
  return Object.keys(officeUpgrades).reduce((sum, id) => {
    const u = OFFICE_UPGRADE_BY_ID[id];
    return sum + (u ? u.chargeMonthly : 0);
  }, 0);
}

/**
 * Aggregate satisfaction boost from all owned office upgrades.
 * Used to bump driver + support staff satisfaction at each tick.
 */
export function getOfficeSatisfactionBoost(officeUpgrades = {}) {
  return Object.keys(officeUpgrades).reduce((sum, id) => {
    const u = OFFICE_UPGRADE_BY_ID[id];
    return sum + (u ? u.satisfactionBoost : 0);
  }, 0);
}

/**
 * Is the office unlocked ? Either through hiring 3+ support OR
 * by purchasing the inv-office-01 investment.
 */
export function isOfficeUnlocked(state) {
  // 18/06/2026 — demande user : plus de déblocage automatique à 3 fonctions
  // support. SEUL l'investissement « Siège social » (inv-office-01) active
  // le bureau. À 3 supports recrutés, un compte à rebours de 45 jours
  // s'enclenche (voir officeSlice.checkHqDeadline).
  return !!(state.activeInvestments || {})['inv-office-01'];
}

/**
 * Pick the next supplier proposal — returns an upgrade id the player
 * doesn't own yet, or null if all are owned. Deterministic FIFO for
 * predictability ; later we may shuffle.
 */
export function pickNextOfficeProposal(officeUpgrades = {}) {
  for (const upg of OFFICE_UPGRADES) {
    if (!officeUpgrades[upg.id]) return upg;
  }
  return null;
}
